import { useEffect, useCallback, useState, useContext, useRef } from 'react';
import { FaFileDownload } from 'react-icons/fa';
import { FcOk, FcInfo, FcCancel } from 'react-icons/fc';
import { FiSquare } from 'react-icons/fi';
import ReactTooltip from 'react-tooltip';

import PropTypes from 'prop-types';

import { AuthContext } from '../../../../actions/auth-context';
import {
  getSW3ThumbnailUrl,
  addSpreadPhotosToOrder,
  deleteSpreadPhotosOfOrder,
  downloadSpreadPhotosFromOrder,
  downloadOrderPhoto,
  updateSpreadPhotosOrder,
} from '../../../../actions/order.service';
import { spreadSequenceComparator } from '../../../../commons/photo-sort';
import {
  canViewSpreadPhotos,
  isSpreadEditingOrBeyond,
} from '../../../../commons/photo-visibility';
import DeleteModal from '../../../../components/delete-modal';
import S3Image from '../../../../components/s3-image';
import { InputFile } from './action-buttons/AddPhotosButton';
import { Container, Photo, Legend } from './EditedPhotos';
import ViewImageModal from './ViewImageModal';

// Lâminas (páginas do fotolivro) montadas pelo editor. Diferente das capturadas,
// as lâminas SÃO visíveis para o cliente final. Edição/remoção só pelo editor
// (ou admin/diretor) com o pedido em 'Em edição de lâminas'. A inspeção das
// lâminas (aprovar/reprovar cada uma) segue a mesma ideia das fotos editadas.
const SpreadPhotos = ({ order, setOrder, getOrder }) => {
  const { setLoading, hasPermissions, hasRoles } = useContext(AuthContext);

  const [thumbnails, setThumbnails] = useState([]);

  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);

  const [firstPhotoToView, setFirstPhotoToView] = useState({});

  const spreadPhotos = (order.photos || []).filter(
    (item) => item.photo_type === 'Lâmina'
  );

  // Só faz sentido mostrar a seção para pedidos de fotolivro. Como o pedido
  // pode ainda não ter lâminas, derivamos pelo produto.
  const isPhotobook = (order.products || []).some(
    (product) => product.type === 'Fotolivro'
  );

  // FEAT-015: tamanho da LÂMINA vem do produto Fotolivro do pedido (todas as
  // lâminas de um álbum têm o mesmo tamanho) — exibido no título da seção.
  const spreadSize = (order.products || []).find(
    (product) => product.type === 'Fotolivro' && product.spread_size
  )?.spread_size;

  // Upload de lâminas: editor (ou admin) com o pedido em 'Em edição de lâminas'.
  const canManage = useCallback(
    () =>
      (hasRoles(['master', 'administrator']) ||
        hasPermissions(['edit_photos', 'edit_order'])) &&
      order.order_status === 'Em edição de lâminas',
    [order]
  );

  // Excluir lâminas: o EDITOR remove em qualquer estágio de edição ('Em edição'
  // ou 'Em edição de lâminas'); admin/diretor em qualquer status. O inspetor NÃO
  // exclui (na inspeção ele aprova/reprova).
  const canRemove = useCallback(() => {
    if (hasRoles(['master', 'administrator'])) return true;

    if (
      (order.order_status === 'Em edição' ||
        order.order_status === 'Em edição de lâminas') &&
      hasPermissions(['edit_photos', 'edit_order'])
    )
      return true;

    return false;
  }, [order]);

  // Ajuste 2 (30/06): antes da aprovação veem master/admin/seller/editor/inspector;
  // após a aprovação só master/admin/editor (+ impressor, que baixa p/ imprimir).
  const canView = canViewSpreadPhotos(
    hasRoles,
    hasPermissions,
    order.order_status
  );

  // Baixar as lâminas: impressor só DURANTE a impressão ('Em impressão');
  // admin/diretor sem restrição de status (FEAT-010).
  const canDownload =
    hasRoles(['master', 'administrator']) ||
    (hasPermissions(['print_photos']) && order.order_status === 'Em impressão');

  const downloadAll = useCallback(async () => {
    setLoading(true);

    await downloadSpreadPhotosFromOrder(order.id, order.person?.full_name);

    setLoading(false);
  }, [order]);

  // Baixar as selecionadas: uma a uma (cada lâmina vira um arquivo), sequencial
  // para não disparar várias requisições simultâneas ao S3/local.
  const downloadSelected = useCallback(async () => {
    setLoading(true);

    await selectedIds.reduce(async (prev, id) => {
      await prev;
      const photo = spreadPhotos.find((item) => item.id === id);
      return downloadOrderPhoto(order.id, id, photo?.file_name);
    }, Promise.resolve());

    setLoading(false);
  }, [selectedIds, spreadPhotos, order]);

  // Baixar UMA lâmina original pelo ícone da barrinha do card (20/07) — mesma
  // mecânica das capturadas.
  const downloadOne = useCallback(
    async (photo_id) => {
      const photo = spreadPhotos.find((item) => item.id === photo_id);

      setLoading(true);

      await downloadOrderPhoto(order.id, photo_id, photo?.file_name);

      setLoading(false);
    },
    [order, spreadPhotos]
  );

  const getLocalBlobThumbnail = useCallback(
    async (photo) => {
      // FEAT-011: presigned URL do S3 (antes baixava o blob pela API).
      const result = await getSW3ThumbnailUrl(order.id, photo.id);

      return {
        id: photo.id,
        blob: result,
        photo_status: photo.photo_status,
        is_cover_photo: photo.is_cover_photo,
        note: photo.note,
        created_at: photo.created_at,
        display_order: photo.display_order,
      };
    },
    [order]
  );

  const prepareThumbnails = useCallback(async () => {
    if (spreadPhotos.length > 0) {
      setIsLoadingPhotos(true);

      const list = spreadPhotos.map((photo) => getLocalBlobThumbnail(photo));

      const result = await Promise.all(list);

      // FEAT-016: sequência do álbum — capa primeiro, depois display_order
      // (legado sem ordem cai por data de upload).
      result.sort(spreadSequenceComparator);

      setThumbnails(result);

      setIsLoadingPhotos(false);
    } else {
      setThumbnails([]);
      setIsLoadingPhotos(false);
    }
  }, [order]);

  useEffect(() => {
    prepareThumbnails();
  }, [order]);

  const onChangePhoto = useCallback(
    async (e) => {
      e.preventDefault();

      setLoading(true);

      let files;

      if (e.dataTransfer) {
        files = e.dataTransfer.files;
      } else if (e.target) {
        files = e.target.files;
      }

      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        formData.append('photos', files[i]);
      }

      const response = await addSpreadPhotosToOrder(order.id, formData);

      if (!response.error) {
        response.delivery_date = new Date(response.delivery_date);

        if (response.payment_first_date) {
          response.payment_first_date = new Date(
            response.payment_first_date.replace(/-/g, ',')
          );
        }

        setOrder(response);
      }

      setLoading(false);
    },
    [order]
  );

  const toggleSelected = useCallback((photo_id) => {
    setSelectedIds((state) =>
      state.includes(photo_id)
        ? state.filter((id) => id !== photo_id)
        : [...state, photo_id]
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((state) =>
      state.length === spreadPhotos.length ? [] : spreadPhotos.map((p) => p.id)
    );
  }, [spreadPhotos]);

  // FEAT-016: reordenação por arrastar e soltar. Mesma janela do canManage
  // ('Em edição de lâminas', editor/admin — sem bypass de status).
  const canReorder = canManage();

  const dragIdRef = useRef(null);

  // Feedback visual do arrasto: card arrastado fica translúcido; o alvo do
  // drop ganha contorno tracejado + leve zoom.
  const [draggingId, setDraggingId] = useState(null);

  const [dragOverId, setDragOverId] = useState(null);

  const dragStyle = (id) => {
    if (!canReorder) return undefined;

    const style = {
      cursor: 'grab',
      transition: 'transform 0.15s ease, opacity 0.15s ease',
    };

    if (draggingId === id) {
      style.opacity = 0.35;
      style.transform = 'scale(0.95)';
    } else if (draggingId && dragOverId === id) {
      style.outline = '3px dashed #ffba00';
      style.outlineOffset = '2px';
      style.transform = 'scale(1.04)';
      style.zIndex = 3;
    }

    return style;
  };

  const reorderOnDrop = useCallback(
    async (targetId) => {
      const draggedId = dragIdRef.current;

      dragIdRef.current = null;

      if (!draggedId || draggedId === targetId) return;

      const current = [...thumbnails];

      const fromIndex = current.findIndex((t) => t.id === draggedId);
      const toIndex = current.findIndex((t) => t.id === targetId);

      if (fromIndex < 0 || toIndex < 0) return;

      const [moved] = current.splice(fromIndex, 1);
      current.splice(toIndex, 0, moved);

      // Feedback imediato na tela; o backend persiste 1..N na sequência.
      setThumbnails(current);

      setLoading(true);

      const response = await updateSpreadPhotosOrder(
        order.id,
        current.map((t) => t.id)
      );

      if (!response.error) {
        response.delivery_date = new Date(response.delivery_date);

        if (response.payment_first_date) {
          response.payment_first_date = new Date(
            response.payment_first_date.replace(/-/g, ',')
          );
        }

        setOrder(response);
      }

      setLoading(false);
    },
    [thumbnails, order]
  );

  const removeSelected = useCallback(async () => {
    setLoading(true);

    const response = await deleteSpreadPhotosOfOrder(order.id, selectedIds);

    if (!response.error) {
      response.delivery_date = new Date(response.delivery_date);

      if (response.payment_first_date) {
        response.payment_first_date = new Date(
          response.payment_first_date.replace(/-/g, ',')
        );
      }

      setOrder(response);

      setSelectedIds([]);
    }

    setLoading(false);
  }, [selectedIds, order]);

  // A seção só faz sentido quando o pedido CHEGOU na fase de lâminas ('Em
  // edição de lâminas' em diante) — antes disso (ex.: 'Em inclusão') é ruído.
  // Exceção: se já existem lâminas (ex.: retrabalho devolveu o pedido para uma
  // fase anterior), a seção continua visível.
  const reachedSpreadPhase =
    isSpreadEditingOrBeyond(order.order_status) || spreadPhotos.length > 0;

  if (!canView || !isPhotobook || !reachedSpreadPhase) return null;

  return (
    <>
      <Container className="mt-5">
        <div className="text-center bg-dark">
          <h5 className="mt-2">
            LÂMINAS DO FOTOLIVRO{spreadSize ? ` — ${spreadSize}` : ''}
          </h5>
        </div>

        <div className="container mt-2">
          <div className="row">
            <div className="col-6">
              {canManage() && (
                <InputFile
                  type="button"
                  className="btn btn-secondary btn-block"
                >
                  add lâminas
                  <input
                    type="file"
                    name="spread_photos"
                    id="spread_photos"
                    multiple="multiple"
                    onChange={onChangePhoto}
                    accept="image/*"
                  />
                </InputFile>
              )}
            </div>

            <div className="col-6 text-end total-fotos">
              {canDownload && spreadPhotos.length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary me-2"
                  onClick={() => downloadAll()}
                >
                  baixar lâminas <FaFileDownload className="mb-1" />
                </button>
              )}
              <p className="d-inline">
                Total de lâminas{' '}
                <span className="badge bg-secondary">
                  {spreadPhotos.length}
                </span>
              </p>
            </div>
          </div>

          {/* Barra de seleção com BOTÕES evidentes (20/07): selecionar/
              desmarcar sempre visíveis; ações desabilitadas até haver seleção. */}
          {(canDownload || canRemove()) && spreadPhotos.length > 0 && (
            <div className="row align-items-center mt-2">
              <div className="col-12 col-md-5">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => toggleSelectAll()}
                >
                  {selectedIds.length === spreadPhotos.length
                    ? 'desmarcar todas'
                    : 'selecionar todas'}
                </button>
                {selectedIds.length > 0 && (
                  <span className="ms-2" style={{ fontSize: '13px' }}>
                    {selectedIds.length} selecionada(s)
                  </span>
                )}
              </div>

              <div className="col-12 col-md-7 text-md-end mt-2 mt-md-0">
                {canDownload && (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary me-2"
                    disabled={selectedIds.length === 0}
                    onClick={() => downloadSelected()}
                  >
                    baixar selecionadas ({selectedIds.length})
                  </button>
                )}

                {canRemove() && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    disabled={selectedIds.length === 0}
                    data-bs-toggle={
                      selectedIds.length > 0 ? 'modal' : undefined
                    }
                    data-bs-target={
                      selectedIds.length > 0
                        ? '#modal-remove-spread-photos'
                        : undefined
                    }
                  >
                    remover selecionadas ({selectedIds.length})
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="row">
            {isLoadingPhotos && (
              <p className="text-center mt-3">Carregando lâminas do S3...</p>
            )}

            {!isLoadingPhotos && spreadPhotos.length === 0 && (
              <div
                className="alert alert-warning text-center mt-2"
                role="alert"
              >
                <b>Atenção!</b> Nenhuma lâmina anexada a este pedido.
              </div>
            )}

            {!isLoadingPhotos && spreadPhotos.length > 0 && (
              <Legend>
                <div>
                  <FcInfo size={15} />
                  <span>Aguarda inspeção</span>
                </div>

                <div>
                  <FcOk size={15} />
                  <span>Aprovada</span>
                </div>

                <div>
                  <FcCancel size={15} />
                  <span>Reprovada</span>
                </div>

                <div>
                  <FiSquare color="#d11515" size={15} />
                  <span>Lâmina de capa</span>
                </div>
              </Legend>
            )}

            {!isLoadingPhotos && canReorder && thumbnails.length > 1 && (
              <p
                className="mb-0 mt-2"
                style={{ fontSize: '12px', textTransform: 'none' }}
              >
                Arraste as lâminas para reordenar — a 1ª posição é a CAPA.
              </p>
            )}

            {!isLoadingPhotos &&
              thumbnails.map((thumbnail, i) => (
                <Photo
                  key={thumbnail.id}
                  className="col-6 col-md-2"
                  is_cover_photo={i === 0}
                  reproved={thumbnail.photo_status === 'Reprovada'}
                  draggable={canReorder}
                  onDragStart={(e) => {
                    // setData é OBRIGATÓRIO para o drag iniciar no Firefox (e
                    // torna o Chrome consistente após re-renders).
                    e.dataTransfer.setData('text/plain', thumbnail.id);
                    e.dataTransfer.effectAllowed = 'move';
                    dragIdRef.current = thumbnail.id;
                    setDraggingId(thumbnail.id);
                  }}
                  onDragEnd={() => {
                    setDraggingId(null);
                    setDragOverId(null);
                  }}
                  onDragOver={
                    canReorder
                      ? (e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          if (dragOverId !== thumbnail.id)
                            setDragOverId(thumbnail.id);
                        }
                      : undefined
                  }
                  onDrop={
                    canReorder
                      ? (e) => {
                          e.preventDefault();
                          setDraggingId(null);
                          setDragOverId(null);
                          reorderOnDrop(thumbnail.id);
                        }
                      : undefined
                  }
                  style={dragStyle(thumbnail.id)}
                  onClick={() =>
                    setFirstPhotoToView({
                      id: thumbnail.id,
                      photo_status: thumbnail.photo_status,
                      note: thumbnail.note,
                      is_cover_photo: thumbnail.is_cover_photo,
                      index: i,
                    })
                  }
                >
                  <div className="card mt-2">
                    {/* FEAT-016: a posição 1 é a CAPA — selo explícito no card. */}
                    {i === 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          top: '4px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 2,
                          backgroundColor: '#d11515',
                          color: '#fff',
                          padding: '1px 10px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                        }}
                      >
                        CAPA
                      </span>
                    )}

                    <S3Image
                      src={thumbnail.blob}
                      alt="lâmina do fotolivro"
                      data-bs-toggle="modal"
                      data-bs-target="#modal-view-spread-image"
                      data-tip={
                        thumbnail.photo_status === 'Reprovada'
                          ? `REPROVADA: ${thumbnail.note || 'sem descrição'}`
                          : undefined
                      }
                      // A <img> tem arrasto nativo próprio que compete com o
                      // arrasto do card (reordenação) — desativado aqui.
                      draggable={false}
                      refreshUrl={() =>
                        getSW3ThumbnailUrl(order.id, thumbnail.id)
                      }
                    />
                    <div className="bg-check">
                      {(canDownload || canRemove()) && (
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={selectedIds.includes(thumbnail.id)}
                          onChange={() => toggleSelected(thumbnail.id)}
                          onClick={(e) => e.stopPropagation()}
                          data-tip="Selecionar"
                        />
                      )}

                      {canDownload && (
                        <FaFileDownload
                          size={22}
                          color="#fff"
                          className="me-2 photo-action"
                          data-tip="Baixar lâmina original"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadOne(thumbnail.id);
                          }}
                        />
                      )}

                      <div className="status">
                        {thumbnail.photo_status === 'Aguarda inspeção' && (
                          <FcInfo size={30} data-tip="Aguarda inspeção" />
                        )}

                        {thumbnail.photo_status === 'Aprovada' && (
                          <FcOk size={30} data-tip="Lâmina aprovada" />
                        )}

                        {thumbnail.photo_status === 'Reprovada' && (
                          <FcCancel
                            size={30}
                            color="red"
                            data-tip={thumbnail.note}
                          />
                        )}
                      </div>

                      <div className="index">
                        <span>{i + 1}</span>
                      </div>
                    </div>
                  </div>
                  <ReactTooltip />
                </Photo>
              ))}
          </div>
        </div>
      </Container>

      <ViewImageModal
        modal_id="modal-view-spread-image"
        cover_noun="lâmina de capa"
        cover_permissions={['edit_order', 'edit_photos', 'edit_inspection']}
        order={{ ...order, photos: spreadPhotos }}
        setOrder={setOrder}
        getOrder={getOrder}
        first_photo_to_view={firstPhotoToView}
        inspection_status="Inspeção de edição de lâminas"
      />

      <DeleteModal
        id_modal="modal-remove-spread-photos"
        item_delete={{ description: 'lâminas selecionadas' }}
        deleteItem={() => removeSelected()}
      />
    </>
  );
};

SpreadPhotos.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.string,
    order_status: PropTypes.string,
    photos: PropTypes.arrayOf(Object),
    products: PropTypes.arrayOf(Object),
    person: PropTypes.shape({ full_name: PropTypes.string }),
  }).isRequired,
  setOrder: PropTypes.func.isRequired,
  getOrder: PropTypes.func.isRequired,
};

export default SpreadPhotos;
